/**
 * Given an array of type T, with a tree structure relating the entities, orders
 * array such that parent entities come before child entities.
 * @param flatTree              Array of type T, where each entity has a relation to 0 or more parents of type T.
 * @param getTIdentifier        Function for getting identifier of each entity, e.g. id or name.
 * @param getParentIdentifiers  Function for getting 0 or more parent identifiers for a given entity.
 * @param getDisregardParentIds (optional) Function for getting any parent ids that should be disregarded when adding t to res.
 * @returns                     Array of type T ordered by entities with no parent first.
 */
export const orderFlattenedTreeParentFirst = <T>(
  flatTree: T[],
  getTIdentifier: (t: T) => string,
  getParentIdentifiers: (t: T) => string[],
  getDisregardParentIds?: (t: T) => string[],
): T[] => {
  // Array of remaining entities, reduced in size on each array iteration
  let remaining = [...flatTree];

  const resIdentifiers = new Set<string>();
  const res: T[] = [];

  while (remaining?.length > 0) {
    const addedIndices: number[] = [];

    for (let i = 0; i < remaining.length; i++) {
      const t = remaining[i];
      const parentIdentifiers = getParentIdentifiers(t);
      const disregardParentIds = getDisregardParentIds?.(t);

      if (
        // If no parents, add current t to res
        !parentIdentifiers?.length ||
        // If all parents already added to res, add current t to res
        parentIdentifiers.every((id) => resIdentifiers.has(id)) ||
        // If remaining parents are all part of disregard ids, add current t to res
        (disregardParentIds?.length &&
          parentIdentifiers
            .filter((id) => !resIdentifiers.has(id))
            ?.every((id) => disregardParentIds.includes(id)))
      ) {
        res.push(t);
        resIdentifiers.add(getTIdentifier(t));
        addedIndices.push(i);
      }
    }

    // Something should be removed on every iteration. If this happens, there might be a circular dependency
    if (!addedIndices?.length) {
      throw new Error(
        'orderFlattenedTreeParentFirst error, possible infinite loop',
      );
    }

    // Removed added entities from the remaining entities array
    remaining = remaining
      .map((t, i) => (addedIndices.includes(i) ? null : t))
      .filter((t) => t);
  }

  return res;
};
